import React, { useCallback, useEffect, useState } from 'react';
import clsx from 'clsx';
import { useIntl } from 'react-intl';
import { useDropzone } from 'react-dropzone';
import { SPOTIFY_HEADER_SIZE } from 'fym-shared/src/music24/spotifyHeader';
import { TypedMemo } from '../../../../common/types';
import { getFullAPIFileURL } from '../../../../common/getFullAPIFileURL';
import { usePostFileMutation } from '../../../../../redux/api/fymAPI';
import { isSuccessfulResult } from '../../../../../redux/api/utils/isSuccessfulResult';
import { SMLButton } from '../../../components/primitives/SMLButton';
import type { ArtistProfile } from './useArtistProfile';
import { spotifyHeaderMessages as messages } from './spotifyHeaderMessages';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the Spotify header; each part uses its own slice of it */
import styles from './SpotifyHeader.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface HeaderPhotoPickerProps {
  accountId: number;
  profile: ArtistProfile;
  /** The file of the photo in use; absent: none yet. */
  fileId: number | undefined;
  photoUrl: string | undefined;
  onPick: (photo: { fileId: number; url: string }) => void;
  /** Under this width the picker warns about a soft photo. Default: the header width (2660). */
  minWidth?: number;
  /** The hint of the picker. Default: the hint of the header. */
  hint?: string;
  /** The sentence of a small photo. Default: the header sentence ("Spotify asks for 2660 px"). */
  smallPhotoText?: (width: number) => string;
}

/** The width of the photo once it loads, so the step can warn about a small photo. */
function usePhotoWidth(url: string | undefined): number | null {
  const [width, setWidth] = useState<number | null>(null);
  useEffect(() => {
    setWidth(null);
    if (url === undefined) {
      return undefined;
    }
    const image = new Image();
    image.onload = () => setWidth(image.naturalWidth);
    image.src = url;
    return () => {
      image.onload = null;
    };
  }, [url]);
  return width;
}

/**
 * "Your photo" of step 3 (design spotify-header a3): the profile photo of the artist with one
 * click, or an upload of a new photo (the account file endpoint, like the kit logo). A photo under
 * 2660 px wide gets the warning; the render scales it up. The artist Profile tools (design
 * artist-profile b3, b4) give their own minimum width and sentences.
 */
const HeaderPhotoPickerComponent: React.FC<HeaderPhotoPickerProps> = ({
  accountId,
  profile,
  fileId,
  photoUrl,
  onPick,
  minWidth = SPOTIFY_HEADER_SIZE.width,
  hint,
  smallPhotoText,
}) => {
  const intl = useIntl();
  const [postFile, { isLoading }] = usePostFileMutation();
  const [failed, setFailed] = useState(false);
  const width = usePhotoWidth(photoUrl);

  const onDrop = useCallback(
    (files: File[]) => {
      const file = files[0];
      if (file === undefined) {
        return;
      }
      setFailed(false);
      const formData = new FormData();
      formData.append('file', file, file.name);
      postFile({ accountId, formData })
        .then((result) => {
          if (isSuccessfulResult(result)) {
            onPick({ fileId: result.data.id, url: getFullAPIFileURL(result.data.url) });
          } else {
            setFailed(true);
          }
        })
        .catch((error: unknown) => {
          console.error(error);
          setFailed(true);
        });
    },
    [accountId, onPick, postFile]
  );
  const { getRootProps, getInputProps, open, isDragActive } = useDropzone({
    onDrop,
    onDropRejected: () => setFailed(true),
    accept: { 'image/jpeg': ['.jpg', '.jpeg'], 'image/png': ['.png'], 'image/webp': ['.webp'] },
    maxFiles: 1,
    disabled: isLoading,
    noClick: true,
    noKeyboard: true,
  });
  const { photo } = profile;
  const usesProfile = photo !== null && fileId === photo.fileId;

  return (
    <div className={styles.picker} data-testid="header-photo-picker">
      <div className={styles.pickerHead}>
        <span className={styles.pickerTitle}>{intl.formatMessage(messages.photoTitle)}</span>
        <span className={styles.pickerHint}>{hint ?? intl.formatMessage(messages.photoHint)}</span>
      </div>
      {photo !== null && (
        <div className={styles.useRow}>
          <span className={styles.useAvatar} style={{ backgroundImage: `url("${profile.avatarUrl ?? photo.url}")` }} />
          <span className={styles.grow}>
            <b>{intl.formatMessage(messages.useProfilePhoto)}</b>
            <span className={styles.small}>
              {intl.formatMessage(messages.useProfilePhotoHint, { artist: profile.name ?? '' })}
            </span>
          </span>
          <SMLButton disabled={usesProfile} onClick={() => onPick(photo)}>
            {intl.formatMessage(usesProfile ? messages.inUse : messages.useIt)}
          </SMLButton>
        </div>
      )}
      {/* eslint-disable-next-line react/jsx-props-no-spreading -- react-dropzone hands its listeners over as a props bag */}
      <div className={clsx(styles.drop, isDragActive && styles.dropActive)} {...getRootProps()}>
        {/* eslint-disable-next-line react/jsx-props-no-spreading -- same props bag, for the hidden file input */}
        <input {...getInputProps()} data-testid="header-photo-input" />
        <span className={styles.grow}>
          <b>{intl.formatMessage(messages.dropTitle)}</b>
          <span className={styles.small}>{intl.formatMessage(messages.dropHint)}</span>
        </span>
        <SMLButton variant="primary" disabled={isLoading} onClick={open}>
          {intl.formatMessage(isLoading ? messages.uploading : messages.choosePhoto)}
        </SMLButton>
      </div>
      {failed && <div className={styles.error}>{intl.formatMessage(messages.uploadFailed)}</div>}
      {width !== null && width < minWidth && (
        <div className={styles.warn} data-testid="header-small-photo">
          {smallPhotoText === undefined ? intl.formatMessage(messages.smallPhoto, { width }) : smallPhotoText(width)}
        </div>
      )}
    </div>
  );
};

export const HeaderPhotoPicker = TypedMemo(HeaderPhotoPickerComponent);
